import type { ReactNode } from "react";
import { Alert } from "./alert.tsx";
import { EmptyState } from "./emptyState.tsx";
import { Skeleton } from "./skeleton.tsx";

export type AsyncState<T> =
  | { status: "loading" }
  | { status: "empty" }
  | { status: "error"; error: unknown }
  | { status: "ready"; data: T };

export interface EmptyConfig {
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
}

export interface AsyncBoundaryProps<T> {
  state: AsyncState<T>;
  /** Override the default loading placeholder (a full-width rect Skeleton). */
  loading?: ReactNode;
  /** Empty-state content. Accepts an EmptyState config or a raw ReactNode. */
  empty?: EmptyConfig | ReactNode;
  /** Override the default error renderer. Receives the raw error + a retry fn (if onRetry provided). */
  error?: (err: unknown, retry: () => void) => ReactNode;
  /** Called when the default error renderer's "Retry" button is clicked. */
  onRetry?: () => void;
  /** Render ready-state content with the resolved data. */
  children: (data: T) => ReactNode;
}

function isEmptyConfig(v: unknown): v is EmptyConfig {
  return !!v && typeof v === "object" && "title" in v;
}

/**
 * Unified wrapper for the three async states (loading / empty / error / ready).
 * Renders one of four branches based on `state.status`; each can be overridden.
 *
 *   <AsyncBoundary state={s} empty={{ title: "No kiosks" }}>
 *     {(kiosks) => <KioskList items={kiosks} />}
 *   </AsyncBoundary>
 */
export function AsyncBoundary<T>({
  state,
  loading,
  empty,
  error,
  onRetry,
  children,
}: AsyncBoundaryProps<T>) {
  switch (state.status) {
    case "loading":
      return <>{loading ?? <Skeleton variant="rect" height={200} />}</>;

    case "empty":
      if (isEmptyConfig(empty)) {
        return <EmptyState icon={empty.icon} title={empty.title} description={empty.description} action={empty.action} />;
      }
      return <>{empty ?? <EmptyState title="No results" />}</>;

    case "error":
      if (error) return <>{error(state.error, onRetry ?? (() => {}))}</>;
      return (
        <Alert variant="error" title="Something went wrong">
          <div>{String((state.error as Error)?.message ?? state.error ?? "Unknown error")}</div>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              style={{
                marginTop: "var(--space-2)",
                padding: "var(--space-1) var(--space-3)",
                fontSize: "var(--text-sm)",
                fontWeight: "var(--font-medium)",
                color: "var(--color-primary-foreground)",
                background: "var(--color-primary)",
                border: "1px solid var(--color-primary)",
                borderRadius: "var(--radius-1)",
                cursor: "pointer",
              }}
            >
              Retry
            </button>
          )}
        </Alert>
      );

    case "ready":
      return <>{children(state.data)}</>;
  }
}
